"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatabaseAiFilterError = void 0;
exports.resolveDatabaseAiFilterCondition = resolveDatabaseAiFilterCondition;
/** The filter does not fit its column; the message is meant for the agent. */
class DatabaseAiFilterError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DatabaseAiFilterError';
    }
}
exports.DatabaseAiFilterError = DatabaseAiFilterError;
const FILTER_FUNCTIONS = {
    contains: 'contains',
    ends_with: 'endsWith',
    equals: 'is',
    greater_than: '>',
    greater_than_or_equal: '≥',
    in: 'isOneOf',
    is_empty: 'isEmpty',
    is_false: 'isFalse',
    is_not_empty: 'isNotEmpty',
    is_true: 'isTrue',
    less_than: '<',
    less_than_or_equal: '≤',
    not_equals: 'isNot',
    not_in: 'isNotOneOf',
    starts_with: 'startsWith',
};
const VALUELESS = new Set([
    'is_empty',
    'is_false',
    'is_not_empty',
    'is_true',
]);
const ARRAY_VALUED = new Set(['in', 'not_in']);
const NUMERIC = new Set([
    'greater_than',
    'greater_than_or_equal',
    'less_than',
    'less_than_or_equal',
]);
/** Size comparisons skip an empty cell, unlike the bare `<` / `>` kernel
 * entries, where Number(null) is 0. */
const RANGE_FUNCTIONS = {
    greater_than: 'greatThan',
    greater_than_or_equal: 'greatThanOrEqual',
    less_than: 'lessThan',
    less_than_or_equal: 'lessThanOrEqual',
};
const DATE_COLUMN_TYPES = new Set(['date', 'created-time', 'updated-time']);
/** Date cells hold epoch milliseconds; before/after skip an empty cell. */
const DATE_RANGE_FUNCTIONS = {
    ...RANGE_FUNCTIONS,
    greater_than: 'after',
    less_than: 'before',
};
/** Date cells hold one instant: equality compares it as a number too, where
 * the text `is` / `isNot` entries would never match an ISO value. */
const DATE_INSTANT_FUNCTIONS = { ...DATE_RANGE_FUNCTIONS, equals: 'equal', not_equals: 'notEqual' };
const ISO_INSTANT = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;
/** Epoch milliseconds of an ISO time with an explicit offset or Z; anything
 * ambiguous (date only, no offset, number, impossible date) is refused. */
function parseDateFilterInstant(operator, value) {
    if (typeof value === 'number')
        throw new DatabaseAiFilterError(`Filter operator "${operator}" on a date column takes an ISO time string with offset, not a number.`);
    const match = typeof value === 'string' ? ISO_INSTANT.exec(value) : null;
    const ms = match ? Date.parse(value) : Number.NaN;
    // Date.parse rolls 2026-02-30 over to March; the calendar day must exist.
    const day = match
        ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
        : null;
    if (!match ||
        !Number.isFinite(ms) ||
        day?.getUTCMonth() !== Number(match[2]) - 1 ||
        day?.getUTCDate() !== Number(match[3]))
        throw new DatabaseAiFilterError(`Filter operator "${operator}" on a date column requires an ISO time with offset, e.g. 2026-10-11T00:00:00+02:00 or 2026-10-10T22:00:00Z.`);
    return ms;
}
const TEXT = new Set([
    'contains',
    'ends_with',
    'starts_with',
]);
/**
 * Maps one agent condition onto the shared projection filter kernel, checking
 * operator and value against the column type. Select values are option ids or
 * labels; the kernel compares both. A relation cell holds row ids: with
 * `relationMembership`, `in` / `not_in` test whether it links one of them.
 */
function resolveDatabaseAiFilterCondition(condition, column, options = {}) {
    const { operator, value } = condition;
    const isValueless = VALUELESS.has(operator);
    if (isValueless !== (value === undefined)) {
        throw new DatabaseAiFilterError(isValueless
            ? `Filter operator "${operator}" does not accept a value.`
            : `Filter operator "${operator}" requires a value.`);
    }
    if (ARRAY_VALUED.has(operator) !== Array.isArray(value)) {
        throw new DatabaseAiFilterError(`Filter operator "${operator}" ${ARRAY_VALUED.has(operator) ? 'requires' : 'does not accept'} an array value.`);
    }
    const relationMembership = options.relationMembership === true &&
        column.type === 'relation' &&
        ARRAY_VALUED.has(operator);
    if (ARRAY_VALUED.has(operator) &&
        column.type !== 'select' &&
        column.type !== 'multi-select' &&
        !relationMembership) {
        throw new DatabaseAiFilterError(`Filter operator "${operator}" requires a select or multi-select column.`);
    }
    const dateColumn = DATE_COLUMN_TYPES.has(column.type);
    const dateFunction = dateColumn
        ? DATE_INSTANT_FUNCTIONS[operator]
        : undefined;
    const instant = dateFunction
        ? parseDateFilterInstant(operator, value)
        : undefined;
    if (NUMERIC.has(operator) &&
        !dateColumn &&
        (column.type !== 'number' || typeof value !== 'number')) {
        throw new DatabaseAiFilterError(`Filter operator "${operator}" requires a number column and numeric value.`);
    }
    if (TEXT.has(operator) && typeof value !== 'string') {
        throw new DatabaseAiFilterError(`Filter operator "${operator}" requires a string value.`);
    }
    if ((operator === 'is_true' || operator === 'is_false') &&
        column.type !== 'checkbox') {
        throw new DatabaseAiFilterError(`Filter operator "${operator}" requires a checkbox column.`);
    }
    const optionAwareEquality = (column.type === 'select' || column.type === 'multi-select') &&
        (operator === 'equals' || operator === 'not_equals');
    const optionMembership = optionAwareEquality || ARRAY_VALUED.has(operator);
    const positiveMembership = operator === 'equals' || operator === 'in';
    const filterFunction = relationMembership
        ? operator === 'in'
            ? 'containsOneOf'
            : 'doesNotContainOneOf'
        : column.type === 'multi-select' && optionMembership
            ? positiveMembership
                ? 'containsOneOf'
                : 'doesNotContainOneOf'
            : optionAwareEquality
                ? positiveMembership
                    ? 'isOneOf'
                    : 'isNotOneOf'
                : column.type === 'number' && operator === 'equals'
                    ? 'equal'
                    : column.type === 'number' && operator === 'not_equals'
                        ? 'notEqual'
                        : (dateFunction ??
                            (column.type === 'number' && NUMERIC.has(operator)
                                ? RANGE_FUNCTIONS[operator]
                                : FILTER_FUNCTIONS[operator]));
    return {
        type: 'filter',
        left: { type: 'ref', name: column.id },
        function: filterFunction,
        args: isValueless
            ? []
            : [
                {
                    type: 'literal',
                    value: optionAwareEquality ? [value] : (instant ?? value),
                },
            ],
    };
}
